import React, { Dispatch, FC, SetStateAction, useCallback, useEffect, useMemo, useState } from "react";

import CalendarHeatmap from "components/dashboard/calendar-heatmap/CalendarHeatmap";
import {
  AnalyticsResultDto,
  AnyFilterExpressionInput,
  HeatData,
  MdmDeviceDto,
  MdmObjectDto,
  SortDirection,
  SortInput,
  useGetAnalyticsByObjectIdPagedLazyQuery,
  useGetAnalyticsByProjectIdPagedLazyQuery,
  useGetAnalyticsHeatmapByObjectIdLazyQuery,
  useGetAnalyticsHeatmapByProjectIdLazyQuery,
} from "gql/generated/types";
import { useCompanyTimezone, useDisplayTimezone } from "hooks/useTimezone";
import { T } from "services/lang/LanguageService";
import { fmtDatetime, getEndOfYesterday, getStartOfYesterday, toZonedDateTime } from "utils/time-utils";
import useCustomColumns from "../hooks/useCustomColumns";
import useProjectContext from "../hooks/useProjectContext";
import useTableFilter from "../hooks/useTableFilter";
import useUserTableDetails from "../hooks/useUserTableDetails";
import { initialFilter, measurementOrder } from "../utils/const";
import { MdmTableName, TableColumn } from "../utils/types";
import {
  apolloErrorToaster,
  measurementUnits,
  renderDeviceLink,
  renderLastMeasurementValue,
  renderObjectLinkInAnalytics,
  renderSeverity,
  searchAnalyticsFilter,
} from "../utils/utils";
import LazyTable from "./LazyTable";
import TableColumnSelection from "./TableColumnSelection";

type ObjectAnalyticsProps = {
  object?: MdmObjectDto;
  tableName: MdmTableName;
};

const ObjectAnalytics: FC<ObjectAnalyticsProps> = ({ object, tableName }) => {
  const displayTz = useDisplayTimezone();
  const companyTz = useCompanyTimezone();

  const { projectId, userSettings, project } = useProjectContext();

  const [dateFrom, setDateFrom] = useState<Date>(() => getStartOfYesterday(companyTz));
  const [dateTo, setDateTo] = useState<Date>(() => getEndOfYesterday(companyTz));

  const tableDetails = useUserTableDetails(userSettings, tableName);

  const [tableOptions, setTableOptions] = useTableFilter(tableName, {
    sortField: "id.date",
    reqSortField: "id.date",
    sortOrder: -1,
  });

  const [getAnalyticsByObject, { data: analytics, loading, previousData: previousAnalytics }] =
    useGetAnalyticsByObjectIdPagedLazyQuery({
      onError: apolloErrorToaster,
    });
  const [getIsolatedAnalyticsByObjectId] = useGetAnalyticsByObjectIdPagedLazyQuery();

  const [getAnalyticsByProject, { data, loading: loading2, previousData: previousAnalytics2 }] =
    useGetAnalyticsByProjectIdPagedLazyQuery({
      onError: apolloErrorToaster,
    });
  const [getIsolatedAnalyticsByProjectId] = useGetAnalyticsByProjectIdPagedLazyQuery();

  const getFilteredData = tableName === "Analytics" ? getAnalyticsByProject : getAnalyticsByObject;
  const getIsolatedData =
    tableName === "Analytics" ? getIsolatedAnalyticsByProjectId : getIsolatedAnalyticsByObjectId;

  const totalAnalytics =
    analytics?.allAnalyticsResultsByObjectIdAndDateRangePaged.pageInfo?.total ??
    data?.allAnalyticsResultsByProjectIdAndDateRangePaged.pageInfo?.total ??
    previousAnalytics?.allAnalyticsResultsByObjectIdAndDateRangePaged.pageInfo?.total ??
    previousAnalytics2?.allAnalyticsResultsByProjectIdAndDateRangePaged.pageInfo?.total ??
    0;
  const isLoading = loading || loading2;
  const analyticsData = useMemo(() => {
    const result =
      analytics?.allAnalyticsResultsByObjectIdAndDateRangePaged.items ??
      data?.allAnalyticsResultsByProjectIdAndDateRangePaged.items ??
      [];
    const previousResult =
      previousAnalytics?.allAnalyticsResultsByObjectIdAndDateRangePaged.items ??
      previousAnalytics2?.allAnalyticsResultsByProjectIdAndDateRangePaged.items ??
      [];
    return (analytics || data ? result : previousResult) as AnalyticsResultDto[];
  }, [analytics, data, previousAnalytics, previousAnalytics2]);

  const measurementKeys = useMemo(
    () => [
      ...(project?.mdmProjectById.deviceConfigs?.reduce((acc, curr) => {
        if (curr?.measurementMappings) {
          curr.measurementMappings.forEach(m => acc.add(m?.key!));
        }
        return acc;
      }, new Set<string>()) ?? []),
    ],
    [project?.mdmProjectById.deviceConfigs]
  );

  const columns: TableColumn<AnalyticsResultDto>[] = useMemo(
    () => [
      {
        field: "id.date",
        header: T("word.date"),
        sortable: true,
        body: rowData => fmtDatetime(rowData.id?.date, displayTz),
        columnKey: "date",
      },
      {
        field: "severity",
        header: T("word.severity"),
        body: renderSeverity,
        sortable: true,
        columnKey: "severity",
      },
      {
        field: "id.analyticsType",
        header: T("word.analytics-type"),
        sortable: true,
        columnKey: "analyticType",
      },
      {
        field: "description",
        header: T("word.description"),
        sortable: true,
        columnKey: "description",
      },
      {
        field: "deviceType",
        header: T("word.device-role"),
        sortable: true,
        columnKey: "deviceType",
      },
      {
        field: "mdmDevice.identifier",
        header: T("word.device"),
        body: rowData => renderDeviceLink(rowData),
        columnKey: "device",
      },
      {
        field: "mdmObject.title",
        header: T("word.object"),
        body: rowData => renderObjectLinkInAnalytics(rowData, projectId!),
        columnKey: "object",
        sortable: true,
        hidden: !!object,
      },
      {
        field: "mdmObject.fullAddress",
        header: T("word.address"),
        columnKey: "address",
        sortable: true,
        hidden: !!object,
      },
      ...measurementKeys
        .sort((a, b) => {
          const indexA = measurementOrder.indexOf(a);
          const indexB = measurementOrder.indexOf(b);
          if (indexA === -1 && indexB === -1) return a.localeCompare(b);
          if (indexA === -1) return 1;
          if (indexB === -1) return -1;
          return indexA - indexB;
        })
        .map(key => ({
          field: `lastMeasurements.${key}` as keyof AnalyticsResultDto,
          header: `${key}${measurementUnits(key) ? `, ${measurementUnits(key)}` : ""}`,
          body: (rowData: AnalyticsResultDto) =>
            renderLastMeasurementValue(rowData as unknown as MdmDeviceDto, key),
          sortable: true,
          columnKey: key,
        })),
    ],
    [measurementKeys, object, projectId]
  );

  const formattedData = useMemo(
    () =>
      analyticsData.map(
        row =>
          ({
            ...row,
            lastMeasurements: row.mdmDevice?.lastMeasurements.reduce(
              (acc, m) => ({ ...acc, [m?.mapping as string]: m?.value }),
              {}
            ),
          }) as AnalyticsResultDto
      ),
    [analyticsData]
  );

  const [
    customColumns,
    setCustomColumns,
    formattedColumns,
    mapCols,
    formatObjectFields,
    handleColumnSave,
    updateLoading,
  ] = useCustomColumns(tableName, columns, formattedData, tableOptions.rows);

  const fetchData = useCallback(
    async (
      take: number | null = null,
      skip: number | null = null,
      sort: SortInput[] | null = null,
      filters: AnyFilterExpressionInput[] | null = null,
      cacheAndNetwork: boolean = false,
      filter: AnyFilterExpressionInput | null = null,
      isolatedFetch: boolean = false
    ) => {
      const fetchFn = isolatedFetch ? getIsolatedData : getFilteredData;
      const resp = await fetchFn({
        variables: {
          input: {
            id: object?.id ?? projectId,
            range: {
              start: dateFrom,
              startInclusive: true,
              end: dateTo,
              endInclusive: true,
            },
            take: take ?? tableOptions.rows,
            skip: skip ?? tableOptions.first,
            sort: sort ?? [
              {
                property: tableOptions.reqSortField,
                direction: tableOptions.sortOrder === 1 ? SortDirection.Asc : SortDirection.Desc,
              },
            ],
            andFilters: filters ?? tableOptions.andFilters,
            filter: filter ?? tableOptions.filter,
          },
        },
        fetchPolicy: cacheAndNetwork ? "cache-and-network" : "cache-first",
      });
      return resp.data;
    },
    [
      dateFrom,
      dateTo,
      getFilteredData,
      getIsolatedData,
      object?.id,
      projectId,
      tableOptions.andFilters,
      tableOptions.filter,
      tableOptions.first,
      tableOptions.reqSortField,
      tableOptions.rows,
      tableOptions.sortOrder,
    ]
  );

  useEffect(() => {
    if (userSettings) {
      fetchData(tableDetails?.rows);
    }
  }, [tableOptions, tableDetails?.rows, userSettings, fetchData]);

  const resetFilters = () => {
    setTableOptions(prev => ({
      ...initialFilter,
      rows: prev.rows,
      sortField: "id.date",
      reqSortField: "id.date",
      sortOrder: -1,
    }));
  };

  const handleSearch = (value: string) => {
    setTableOptions(prev => ({
      ...prev,
      filter: value ? searchAnalyticsFilter(value) : null,
      first: 0,
      search: value,
    }));
  };

  return (
    <div className="mt-4">
      <CalendarHeatmapWrapper
        tableName={tableName}
        queryId={object?.id ?? projectId!}
        dateFrom={dateFrom}
        dateTo={dateTo}
        setDateFrom={setDateFrom}
        setDateTo={setDateTo}
      />
      <LazyTable
        columns={formattedColumns}
        data={formatObjectFields}
        tableName={tableName}
        buttons={
          <TableColumnSelection
            customColumns={customColumns}
            tableName={tableName}
            setCustomColumns={setCustomColumns}
            handleColumnSave={handleColumnSave}
            mapCol={mapCols}
            loading={updateLoading}
          />
        }
        containerName="AnalyticsResult"
        loading={isLoading}
        setTableOptions={setTableOptions}
        resetFilters={resetFilters}
        tableOptions={tableOptions}
        totalItems={totalAnalytics}
        fetchData={fetchData}
        onSearch={handleSearch}
        setCustomColumns={setCustomColumns}
      />
    </div>
  );
};

export default ObjectAnalytics;

type CalendarHeatmapWrapperProps = {
  tableName: MdmTableName;
  queryId: string;
  dateFrom: Date;
  dateTo: Date;
  setDateFrom: Dispatch<SetStateAction<Date>>;
  setDateTo: Dispatch<SetStateAction<Date>>;
};

const CalendarHeatmapWrapper: FC<CalendarHeatmapWrapperProps> = ({
  tableName,
  queryId,
  dateFrom,
  dateTo,
  setDateFrom,
  setDateTo,
}) => {
  const displayTz = useDisplayTimezone();
  const [getHeatmapByObject, { data: objectHeatmap }] = useGetAnalyticsHeatmapByObjectIdLazyQuery({
    onError: apolloErrorToaster,
  });

  const [getHeatmapByProject, { data: projectHeatmap }] = useGetAnalyticsHeatmapByProjectIdLazyQuery({
    onError: apolloErrorToaster,
  });

  const getHeatmapData = tableName === "Analytics" ? getHeatmapByProject : getHeatmapByObject;

  const analyticsData: HeatData[] = useMemo(
    () => [
      ...(objectHeatmap?.analyticsHeatmapByObjectIdAndDateRange ??
        projectHeatmap?.analyticsHeatmapByProjectIdAndDateRange ??
        []),
    ],
    [objectHeatmap, projectHeatmap]
  );

  useEffect(() => {
    getHeatmapData({
      variables: {
        id: queryId,
        range: {
          start: dateFrom,
          startInclusive: true,
          end: dateTo,
          endInclusive: true,
        },
      },
    });
  }, [dateFrom, dateTo, getHeatmapData, queryId]);

  const formatHeatData = analyticsData.map(data => ({
    value: data.value,
    date: toZonedDateTime(data.date, displayTz)!.toInstant(),
  }));

  return (
    <CalendarHeatmap
      startDate={dateFrom}
      endDate={dateTo}
      dataset={formatHeatData}
      setStartDate={setDateFrom}
      setEndDate={setDateTo}
      highValueColor="ed0000"
      lowValueColor="fde3e3"
      minValue={0}
      maxValue={100}
      staticMinMaxColors
      staticMinMaxTitles={false}
      staticMinMaxValues
      isCustomDataset
      showDateSelection
      ignoreCurrentDay={false}
    />
  );
};
